import type { TemplatePage } from '../templates';

const filled = [
  { item: 'Retaining wall, 40 ft long x 2.5 ft high, segmental block, gravel backfill and drain pipe', amount: '$4,600' },
  { item: 'Planting: 6 hydrangeas (3 gal), 3 ornamental grasses (2 gal), installed with compost', amount: '$720' },
  { item: 'Shredded hardwood mulch, 4 cubic yards, spread 3 in deep over new and existing beds', amount: '$380' },
  { item: 'Optional: drip line for the new beds, connected to the existing controller', amount: '$540' },
];

const faq = [
  {
    q: 'Does this landscaping template work for hardscape and softscape?',
    a: 'Yes. It has separate sections for hardscape items (walls, patios, steps) and for plants and beds, because they are measured and warranted differently.',
  },
  {
    q: 'How long should a landscaping quote be valid?',
    a: 'Long enough for the customer to decide, short enough that material prices have not moved. The template has a "good until" date and a line saying material prices are held until then.',
  },
  {
    q: 'Should I include a plant warranty?',
    a: 'Many landscapers offer one, with conditions such as regular watering. If you do, the template has a blank for the length and the conditions; if you do not, say so to avoid assumptions.',
  },
  {
    q: 'Can the homeowner sign this quote online?',
    a: 'In Scopie this becomes a quote emailed to the homeowner as a link with your company name. They can tick the drip line or other extras, read your terms and sign.',
  },
];

const page: TemplatePage = {
  slug: 'landscaping-quote-template',
  path: '/templates/landscaping-quote-template',
  primaryKeyword: 'landscaping quote template',
  secondaryKeywords: ['landscaping estimate template', 'landscape proposal template', 'hardscape estimate template'],
  title: 'Free Landscaping Quote Template (PDF + Example) | Scopie', // 55
  metaDescription:
    'A free landscaping quote template with separate hardscape and planting sections, material quantities, plant warranty and access terms, and an example.', // 151
  h1: 'Landscaping quote template',
  answerFirst:
    'A landscaping quote should separate hardscape from planting, give material quantities in the units you buy them, and spell out access, utilities and the plant warranty. This free template does all three, with a filled-in example for a retaining wall, new beds and mulch that you can adapt to your own jobs.',
  lastReviewed: '2026-10-07',
  schemaType: 'DigitalDocument',
  leadSheet: true,
  blocks: [
    {
      type: 'sample-quote',
      id: 'filled',
      h2: 'What a finished landscaping quote looks like',
      label: 'Example: retaining wall, planting and mulch',
      lines: filled,
      total: '$6,240',
      totalLabel: 'Total if the drip line is added',
      note: 'Illustrative figures for a single project.',
    },
    {
      type: 'document',
      id: 'template',
      h2: 'The blank template',
      intro: 'Use the hardscape and planting sections that apply; delete the rest.',
      pdfName: 'landscaping-quote-template',
      sections: [
        { heading: 'Contractor', lines: ['[Company] · [Phone] · [Email] · [License number, if required]'] },
        { heading: 'Project', lines: ['[Client] · [Property address]', 'Quote [number] · [date] · good until [date]; material prices held until that date'] },
        {
          heading: 'Hardscape',
          lines: [
            '[Patio / walk / wall / steps]: [dimensions], [material and color]',
            'Excavation depth [inches] · base [type] [inches] compacted · edge restraint [type]',
            'Drainage: [pipe, gravel, outlet location] · price [amount]',
          ],
        },
        {
          heading: 'Plants and beds',
          lines: ['[Quantity] x [plant name], [container size] · price [amount]', 'Soil prep: [compost / amendment], [cubic yards]', 'Mulch: [type, color], [cubic yards], [depth] · price [amount]'],
        },
        { heading: 'Removal and disposal', lines: ['[Sod / old shrubs / stone] removed and hauled: [loads] · price [amount]'] },
        {
          heading: 'Site and access',
          lines: ['Equipment access through [gate width / side yard]; lawn protection: [mats / none]', 'Utility locate requested by [us / client]; irrigation lines marked by [client]'],
        },
        {
          heading: 'Terms',
          lines: [
            'Plant warranty: [length], conditional on [watering / care]; or no warranty.',
            'Changes to size, layout or materials after acceptance are priced before the work.',
            'Deposit: [amount], due on acceptance, for materials. Cancellation: [your terms].',
          ],
        },
        { heading: 'Acceptance', lines: ['[Client name] · [Signature] · [Date]'] },
      ],
    },
    {
      type: 'list',
      id: 'units',
      h2: 'Use the units you buy materials in',
      style: 'bullets',
      intro: 'Matching the quote to your supplier\'s units makes ordering and re-quoting faster:',
      items: [
        { title: 'Mulch, soil and gravel', text: 'Cubic yards, with the depth you will spread.' },
        { title: 'Pavers and wall block', text: 'Square feet of face or surface, plus caps by linear foot.' },
        { title: 'Plants', text: 'Count and container size, such as 3-gallon or 15-gallon.' },
        { title: 'Sod', text: 'Square feet or pallets.' },
      ],
    },
    {
      type: 'list',
      id: 'mistakes',
      h2: 'Common landscaping quote mistakes',
      style: 'mistakes',
      items: [
        { title: 'Base and drainage hidden in the price', text: 'They are where a wall or patio lasts or fails. Show them.' },
        { title: 'Plant warranty assumed', text: 'A customer who stops watering expects free replacements unless the terms say otherwise.' },
        { title: 'No access note', text: 'A narrow side gate turns a skid-steer job into a wheelbarrow job.' },
      ],
    },
    {
      type: 'text',
      id: 'phases',
      h2: 'Big designs in phases',
      paragraphs: [
        'When a full design is more than the homeowner wants to spend this year, split the quote into phases: the patio and drainage now, planting and lighting next season. Each phase gets its own lines and its own price, so the customer can approve the first step without walking away from the plan, and you keep the rest of the work in view.',
      ],
    },
    { type: 'faq', id: 'faq', h2: 'Landscaping template questions', items: faq },
  ],
  faq,
  internalLinks: [
    { href: '/for/landscaping', anchor: 'Scopie for landscaping businesses', status: 'live' },
    { href: '/for/lawn-care', anchor: 'Scopie for lawn care', status: 'live' },
    { href: '/templates/fence-estimate-template', anchor: 'fence estimate template', status: 'live' },
  ],
  cta: {
    heading: 'Quote the next yard from the site visit',
    text: 'Keep your materials, units and warranty terms in Scopie and email each landscaping quote as a link.',
    button: 'Start free',
    href: '/register',
    fine: '14-day free trial. No credit card required.',
  },
};

export default page;
